import { Permission } from '@bookora/shared';

import { sectionFeature } from '@/features/plan/section-features';

import { tourFeatureOn } from '../resolve-steps';
import type { TourDef } from './types';

/**
 * Lots, batch numbers and expiry dates are the «انبار با سری ساخت و تاریخ
 * انقضا» plan feature (owner D6): a clinic without it keeps SIMPLE stock
 * (quantity in, consumption out). Once plan access has answered, the steps
 * word themselves by it (until then they keep their words — the screen is
 * still drawn open). No step is dropped: the alerts tab is on every plan,
 * because low stock is an alert on simple stock too — only its expiry half
 * is the plan feature's (the reviewed design dropped the whole step, which
 * would hide the low-stock alerts a «پایه» clinic does have).
 */
const LOTS = sectionFeature('inventory:lots');

/**
 * `/inventory`. Copy fixes only since version 1, so the version stays (a bump
 * re-offers the tour as «به‌روز شده» and spends the shared budget on words):
 * the tabs step now names «کالاها», where a new clinic defines its products,
 * and the receive step no longer says the purchase expense depends on
 * writing a price — the price is required, and the expense follows the
 * form's «هزینهٔ «خرید مواد» هم ثبت شود» switch, on by default.
 */
export const INVENTORY_TOUR: TourDef = {
  key: 'inventory',
  version: 1,
  surface: 'page',
  title: 'انبار',
  hubDescription: 'کالاها، موجودی و سری‌های ساخت، هشدارهای انقضا و ثبت ورود کالا.',
  offer: {
    body: 'در چند قدم کوتاه نشونتون می‌دیم موجودی، تاریخ انقضا و ورود کالا چطور کار می‌کنه.',
  },
  route: { path: '/inventory', match: (pathname) => pathname === '/inventory' },
  requires: { all: [Permission.INVENTORY_VIEW] },
  finish: {
    label: 'ثبت ورود کالا',
    // `?receive=1` opens the receive drawer (inventory-page.tsx).
    to: '/inventory?receive=1',
    requires: { all: [Permission.INVENTORY_RECEIVE] },
  },
  autoOffer: true,
  maxSteps: 3,
  minSteps: 2,
  steps: [
    {
      id: 'tabs',
      anchor: 'inventory.tabs',
      placement: 'bottom',
      title: 'تب‌های انبار',
      body: (ctx) => {
        const lead =
          tourFeatureOn(LOTS) !== false
            ? 'موجودی هر کالا در «موجودی» است و هر سری ساخت با تاریخ انقضای خودش در «سری‌های ساخت». هر ورود و خروج در «گردش انبار» می‌مونه.'
            : 'موجودی هر کالا در «موجودی» است و هر ورود و خروج در «گردش انبار» می‌مونه.';
        return ctx.can(Permission.INVENTORY_MANAGE_MATERIALS)
          ? `${lead} کالای جدید رو در «کالاها» تعریف کنید.`
          : lead;
      },
    },
    {
      id: 'receive',
      anchor: 'inventory.receive',
      placement: 'bottom-end',
      requires: { all: [Permission.INVENTORY_RECEIVE] },
      title: 'ثبت ورود کالا',
      // Only a cost-viewer sees the expense it books.
      body: (ctx) => {
        const lead =
          tourFeatureOn(LOTS) !== false
            ? 'هر خرید رو با شمارهٔ سری ساخت و تاریخ انقضا ثبت کنید.'
            : 'هر خرید رو با کالا و مقدارش ثبت کنید تا به موجودی انبار اضافه بشه.';
        return ctx.can(Permission.FINANCE_VIEW_COSTS)
          ? `${lead} هزینهٔ خرید هم خودکار در حسابداری ثبت می‌شه؛ اگر نمی‌خواید ثبت بشه، گزینهٔ ثبت هزینهٔ خرید مواد رو در همون فرم خاموش کنید.`
          : lead;
      },
    },
    {
      id: 'alerts',
      anchor: { wide: 'inventory.tab-alerts', compact: 'inventory.tabs' },
      placement: 'bottom',
      title: 'هشدارها',
      // On every plan: low stock is an alert on simple stock too. Only the
      // expiring lots are the plan feature's, so only they leave the words.
      body: (ctx) => {
        const what =
          tourFeatureOn(LOTS) !== false
            ? 'کالاهای رو به اتمام و سری‌های نزدیک به انقضا'
            : 'کالاهای رو به اتمام';
        return ctx.viewport === 'compact'
          ? `در تب «هشدارها» ${what} جمع می‌شن.`
          : `${what} اینجا جمع می‌شن. عدد کنار تب، تعداد مواردی است که نیاز به رسیدگی دارن.`;
      },
    },
  ],
};
